import { type Role } from '../../entities/role';
import { type User } from '../../entities/user';
import { GetUsersQuery } from '../get-users';

import {
  getNextUserId,
  sharedState,
  updateRoleUserCounts,
} from './shared-state';

// Helper function to get role by ID
function getRoleFromState(
  roleId: string,
): { id: string; name: string } | undefined {
  const role = sharedState.roles.find((r) => r.id === roleId);
  return role ? { id: role.id, name: role.name } : undefined;
}

// User CRUD functions
export function getAllUsers(): User[] {
  return [...sharedState.users];
}

export function getUsersPaginated(
  input: GetUsersQuery.Input,
): GetUsersQuery.Output {
  const allUsers = sharedState.users;
  const totalCount = allUsers.length;

  const { page, pageSize } = input;
  const offset = (page - 1) * pageSize;
  const users = allUsers.slice(offset, offset + pageSize);

  // Calculate pagination info
  const currentPage = page;
  const previousPage = currentPage > 1 ? currentPage - 1 : undefined;
  const hasMoreItems = offset + pageSize < totalCount;
  const nextPage = hasMoreItems ? currentPage + 1 : undefined;

  return {
    totalCount,
    previousPage,
    currentPage,
    nextPage,
    users,
  };
}

export function getUserById(id: string): User | undefined {
  return sharedState.users.find((user) => user.id === id);
}

export function createUser(data: {
  firstName: string;
  lastName: string;
  email: string;
  roleId: string;
}): { user: { id: string } } | null {
  const role = getRoleFromState(data.roleId);
  if (!role) {
    return null;
  }

  const newUser: User = {
    id: getNextUserId(),
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    active: true,
    lastLogin: new Date(),
    role,
  };

  sharedState.users.push(newUser);
  updateRoleUserCounts();

  return { user: { id: newUser.id } };
}

export function updateUser(
  id: string,
  data: {
    active: boolean;
    firstName: string;
    lastName: string;
    email: string;
    roleId: string;
  },
): { user: { id: string } } | null {
  const userIndex = sharedState.users.findIndex((user) => user.id === id);
  if (userIndex === -1) {
    return null;
  }

  const role = getRoleFromState(data.roleId);
  if (!role) {
    return null;
  }

  sharedState.users[userIndex] = {
    ...sharedState.users[userIndex],
    active: data.active,
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    role,
  };

  updateRoleUserCounts();

  return { user: { id } };
}

export function deleteUser(id: string): boolean {
  const userIndex = sharedState.users.findIndex((user) => user.id === id);
  if (userIndex === -1) {
    return false;
  }

  sharedState.users.splice(userIndex, 1);
  updateRoleUserCounts();

  return true;
}

// Export roles for other modules to use
export function getAllRolesFromUserState(): Role[] {
  return [...sharedState.roles];
}
